import { reactive, toRefs } from 'vue'
import { Threads, Thread, Question, Message, InProgressIds, CurrentThread, Answer } from '@/components/chat/types.ts'
import { defineStore } from 'pinia'
import { instance, getApiDomain } from '@cognitivecredit/ui-utils'

const defaultInProgressIds: InProgressIds = {
  questionId: undefined,
  messageId: undefined,
  sessionId: undefined,
  streamingId: undefined,
}

const getNewMessage = (question: Question): Message => {
  return {
    id: crypto.randomUUID(),
    question,
    isCollapsed: false,
    isStreaming: true,
    answer: undefined,
    sources: [],
  }
}

export const useChatAppStore = defineStore('ChatApp', () => {
  const state = reactive({
    threads: [] as Threads,
    currentThread: undefined as CurrentThread | undefined,
    allCollapsed: false,
    inProgressIds: defaultInProgressIds,
  })

  const fetchThreadsHistory = async () => {
    try {
      const req = await instance({ apiDomain: getApiDomain('chat-ai') })
      state.threads = (await req.get('/ai/threads')).data
    } catch (error) {
      console.error('Error fetching chat history threads:', error)
    }
  }

  const addThreadToHistory = (thread: Thread) => {
    const indexOfFirstUnpinned = state.threads.findIndex((t) => !t.pin)
    if (indexOfFirstUnpinned === -1) {
      state.threads.unshift(thread)
    } else {
      state.threads.splice(indexOfFirstUnpinned, 0, thread)
    }
  }

  const updateThreadsHistory = (id: string, key: string, value: any) => {
    const index = state.threads.findIndex((thread) => thread.id === id)
    if (index !== -1) {
      state.threads[index][key] = value
    }
  }

  const setInProgressIds = (ids?: {
    questionId?: string
    messageId?: string
    sessionId?: string
    streamingId?: string
  }) => {
    if (!ids) {
      state.inProgressIds = { ...defaultInProgressIds }
    } else {
      state.inProgressIds = { ...state.inProgressIds, ...ids }
    }
  }

  const setCurrentThread = (thread?: CurrentThread) => {
    setInProgressIds()
    state.currentThread = thread
  }

  const createCurrentThread = (id: string, title: string, question: Question, sessionId: string, createdAt: string) => {
    const newMessage = getNewMessage(question)
    setInProgressIds({ questionId: question.id, messageId: newMessage.id, sessionId, streamingId: newMessage.id })
    const newThread: CurrentThread = {
      id,
      title,
      pin: false,
      deleted: false,
      sessionId: sessionId,
      createdAt: createdAt,
      messages: [newMessage],
    }
    state.currentThread = newThread
    addThreadToHistory(newThread)
  }

  const addMessageToCurrentThread = (question: Question) => {
    if (!state.currentThread) {
      return
    }
    const msg = getNewMessage(question)
    setInProgressIds({ questionId: question.id, messageId: msg.id, streamingId: msg.id })
    // Find an existing message without an id (a placeholder) and replace it
    const messageIndex = state.currentThread?.messages.findIndex((m) => !m.question.id)
    state.currentThread.messages[messageIndex] = msg
    return msg
  }

  const updateMessage = (
    thread: CurrentThread,
    messageId: string,
    update: { key: string; value: any; nestedKey?: string },
  ) => {
    const i = thread.messages.findIndex((m) => m.id === messageId)
    if (i === -1) {
      return
    }

    const msg = thread.messages[i]

    const updated: Message = { ...msg }

    if (update.nestedKey) {
      updated[update.key] = {
        ...msg[update.key],
        [update.nestedKey]: update.value,
      }
    } else {
      updated[update.key] = update.value
    }

    thread.messages[i] = updated
  }

  const updateCurrentThreadMessageAnswer = (answer: Answer) => {
    if (!state.currentThread || !state.inProgressIds.messageId) {
      return
    }

    updateMessage(state.currentThread, state.inProgressIds.messageId, {
      key: 'answer',
      value: answer,
    })

    setInProgressIds({ questionId: undefined, messageId: undefined, sessionId: state.inProgressIds.sessionId })
  }

  const updateCurrentThreadMessages = (messageId: string, key: string, value: any, nestedKey?: string) => {
    if (!state.currentThread) {
      return
    }
    updateMessage(state.currentThread, messageId, { key, value, nestedKey })

    if (key === 'isCollapsed' && !value && state.allCollapsed) {
      state.allCollapsed = false
    }
  }

  const setCollapseAll = () => {
    state.allCollapsed = !state.allCollapsed
    state.currentThread?.messages.forEach((m) => {
      m.isCollapsed = state.allCollapsed
    })
  }

  const resetStore = () => {
    state.currentThread = undefined
    state.allCollapsed = false
    state.inProgressIds = defaultInProgressIds
  }

  return {
    ...toRefs(state),
    fetchThreadsHistory,
    addThreadToHistory,
    updateThreadsHistory,
    setCurrentThread,
    createCurrentThread,
    addMessageToCurrentThread,
    updateCurrentThreadMessageAnswer,
    updateCurrentThreadMessages,
    setCollapseAll,
    setInProgressIds,
    resetStore,
  }
})
